// ── Server-side fee lists and reports — the wire contract (WP-9) ─────────────
//
// The Finance page used to fetch `GET /fees/payments?limit=5000` (a deep
// include, parent phone / address / blood group on every row, the count
// skipped) and fold it in the browser: Dues, Transactions and every filtered
// student list. A school past 5,000 fee rows silently lost the rows ordered
// last by `dueDate`, and nothing on screen said so (fees audit P1-10).
//
// These are the schemas and types the new endpoints and the web hooks share, so
// the query a screen builds and the query the route accepts are ONE definition,
// and a payload the API sends is checked against the same schema the contract
// tests parse it with. Routes live in `apps/api/src/routes/fees/reports.ts`,
// the SQL in `apps/api/src/lib/fee-lists.ts`.
//
// What every figure means (one definition each, owned by `lib/fee-snapshot.ts`):
//   • billed / concession / fine / net — Σ over the student's NON-WAIVED rows in
//     the academic scope. `net = billed − concession + fine` (a row's
//     `netAmount`), and Σ net over every student is the snapshot's
//     `billedThisYear`.
//   • paid — Σ `paidAmount` over PAID + PARTIAL rows (cash is permanent, so a
//     withdrawn student's receipts still count when `includeWithdrawn` is on).
//   • due — Σ per-row residual `max(0, net − paid)` over PENDING + PARTIAL rows
//     of a student still on the roll, plus carried-forward `PreviousFeeDue`.
//     Σ due over the roll is the snapshot's `outstandingTotal`.
//   • overdue — the slice of `due` whose due DAY has passed in IST (the due date
//     is before the start of today in Asia/Kolkata); carried-forward dues are
//     past due by definition. Σ overdue is the snapshot's `overdueTotal`.
//
// No parent phone, address, blood group or e-mail travels on any list row: a
// list is not a contact sheet. Search may MATCH a parent's name or phone, but
// the number never comes back.
//
// Leaf module — imports only zod and the fee-head vocabulary, so the barrel
// re-export in `types/index.ts` cannot form a cycle.
import { z } from "zod";
import { FEE_HEADS } from "./fee-heads.js";

// ── Shared query primitives ──────────────────────────────────────────────────

/** `?x=` and whitespace mean "not sent" — a cleared filter box must not 400. */
export const feeQueryBlankToUndefined = (v: unknown): unknown => {
  if (typeof v !== "string") return v;
  const t = v.trim();
  return t === "" ? undefined : t;
};

const academicLabel = z
  .string()
  .regex(/^\d{4}-\d{2}$/, "academic must look like 2026-27");

const idParam = z.string().trim().min(1).max(64);

/** A real calendar day, `YYYY-MM-DD` (IST). `2026-02-31` is a 400, not a rollover. */
export const feeDayKey = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "must be a day like 2026-10-01")
  .refine((s) => {
    const d = new Date(`${s}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
  }, "must be a real calendar day");

const queryBool = z.enum(["true", "false"]);

const optionalText = (max: number) =>
  z.preprocess(feeQueryBlankToUndefined, z.string().trim().max(max).optional());

// ── The one status rule ──────────────────────────────────────────────────────

/** The four statuses a student's fee card can wear — `pages/finance/fee-card-status.ts`'s `FeeCardStatus`, upper-cased on the wire. */
export const FEE_STUDENT_STATUSES = ["OVERDUE", "PARTIAL", "PENDING", "PAID"] as const;
export type FeeStudentStatus = (typeof FEE_STUDENT_STATUSES)[number];

/** Sub-paisa slack so a float residue like 1e-12 never reads as "owes money". */
export const FEE_LIST_EPSILON = 0.005;

/**
 * A student's folded fee status — the rule SQL renders in `lib/fee-lists.ts`
 * and the Finance page's `feeCardStatus` reproduces from its own fold, pinned
 * against each other by tests on both sides.
 *
 *   • anything overdue (a past-due installment still owing, or a carried-forward
 *     due) is OVERDUE — part-paying an installment does not move its date;
 *   • otherwise still owing: PARTIAL once any money has been received, else PENDING;
 *   • nothing owing is PAID — a fully waived student reads PAID too (cleared).
 */
export function deriveFeeStudentStatus(f: { due: number; overdue: number; paid: number }): FeeStudentStatus {
  if (f.overdue > FEE_LIST_EPSILON) return "OVERDUE";
  if (f.due > FEE_LIST_EPSILON) return f.paid > FEE_LIST_EPSILON ? "PARTIAL" : "PENDING";
  return "PAID";
}

// ── GET /fees/students ───────────────────────────────────────────────────────

export const FEE_STUDENT_SORTS = [
  "name",
  "admissionNo",
  "class",
  "billed",
  "paid",
  "due",
  "overdue",
  "nextDue",
  "daysPastDue",
] as const;
export type FeeStudentSort = (typeof FEE_STUDENT_SORTS)[number];

export const FEE_STUDENT_TYPES = ["BOARDING", "DAY"] as const;

const statusList = z.preprocess(
  feeQueryBlankToUndefined,
  z
    .string()
    .transform((v) => v.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean))
    .pipe(z.array(z.enum(FEE_STUDENT_STATUSES)).min(1))
    .optional(),
);

/** The filter half, shared with `GET /fees/students.csv` (which takes no paging). */
export const FeeStudentsFilterSchema = z.object({
  /** `YYYY-YY` — the snapshot's `feeCashScope`. Omit for every year, like the snapshot. */
  academic: z.preprocess(feeQueryBlankToUndefined, academicLabel.optional()),
  /** One or a comma list: `OVERDUE,PARTIAL`. */
  status: statusList,
  classId: z.preprocess((v) => (v === "all" ? undefined : feeQueryBlankToUndefined(v)), idParam.optional()),
  /** A section; `-` means "no section" (a classroom whose students have none). */
  section: optionalText(32),
  /** `DAY` is everyone who is NOT boarding, untyped included — the Finance page's own rule. `all` is "no filter". */
  studentType: z.preprocess(
    (v) => (v === "all" ? undefined : feeQueryBlankToUndefined(v)),
    z.enum(FEE_STUDENT_TYPES).optional(),
  ),
  /** Name, admission no., parent name, class, section, or phone digits. Never echoed back. */
  search: optionalText(120),
  /** Only students who still owe money. */
  dueOnly: z.preprocess(feeQueryBlankToUndefined, queryBool.optional()),
  /** Only students with an overdue balance — the population `defaultersCount` counts. */
  overdueOnly: z.preprocess(feeQueryBlankToUndefined, queryBool.optional()),
  /** Keep students who left the roll: their billed/paid stay (cash is permanent), their due is 0. */
  includeWithdrawn: z.preprocess(feeQueryBlankToUndefined, queryBool.optional()),
  sort: z.preprocess(feeQueryBlankToUndefined, z.enum(FEE_STUDENT_SORTS).default("name")),
  dir: z.preprocess(feeQueryBlankToUndefined, z.enum(["asc", "desc"]).default("asc")),
});
export type FeeStudentsFilter = z.output<typeof FeeStudentsFilterSchema>;

export const FEE_STUDENTS_MAX_PAGE_SIZE = 200;
export const FEE_STUDENTS_DEFAULT_PAGE_SIZE = 25;

export const FeeStudentsQuerySchema = FeeStudentsFilterSchema.extend({
  page: z.preprocess(feeQueryBlankToUndefined, z.coerce.number().int().min(1).max(100_000).default(1)),
  pageSize: z.preprocess(
    feeQueryBlankToUndefined,
    z.coerce.number().int().min(1).max(FEE_STUDENTS_MAX_PAGE_SIZE).default(FEE_STUDENTS_DEFAULT_PAGE_SIZE),
  ),
});
export type FeeStudentsQuery = z.output<typeof FeeStudentsQuerySchema>;
/** What a caller may send — everything optional, strings or numbers. */
export type FeeStudentsQueryInput = z.input<typeof FeeStudentsQuerySchema>;

const money = z.number();

export const FeeStudentRowSchema = z.object({
  studentId: z.string(),
  name: z.string(),
  admissionNo: z.string(),
  avatar: z.string().nullable(),
  classId: z.string().nullable(),
  className: z.string().nullable(),
  section: z.string().nullable(),
  /** `BOARDING` / `DAY_SCHOLAR` as stored; null = never recorded. */
  studentType: z.string().nullable(),
  /** False for a student off the roll (only present with `includeWithdrawn`). */
  onRoll: z.boolean(),
  billed: money,
  concession: money,
  fine: money,
  /** `billed − concession + fine`. */
  net: money,
  paid: money,
  due: money,
  overdue: money,
  /** The carried-forward `PreviousFeeDue` half of `due`. */
  previousDue: money,
  status: z.enum(FEE_STUDENT_STATUSES),
  /** Fee rows (any status) and how many of them are settled (PAID or WAIVED). */
  items: z.number().int(),
  itemsSettled: z.number().int(),
  /** The earliest open installment (by installment number, then due date). Null when nothing is open. */
  next: z
    .object({
      label: z.string(),
      /** What is still owed on that one row. */
      due: money,
      /** IST day `YYYY-MM-DD`; null when the row has no due date. */
      dueDate: z.string().nullable(),
      overdue: z.boolean(),
    })
    .nullable(),
  /** Oldest overdue installment's IST day (or the oldest carried-forward due), null when nothing is overdue. */
  oldestOverdueDate: z.string().nullable(),
  /** Whole IST days since `oldestOverdueDate`; null when it is unknown. */
  daysPastDue: z.number().int().nullable(),
  /** IST day of the latest receipt on this student's rows. */
  lastPaidDate: z.string().nullable(),
});
export type FeeStudentRow = z.infer<typeof FeeStudentRowSchema>;

export const FeeStudentsSummarySchema = z.object({
  /** Students matching the filters — across ALL pages. */
  students: z.number().int(),
  billed: money,
  concession: money,
  fine: money,
  net: money,
  paid: money,
  due: money,
  overdue: money,
  /** Students who still owe — the snapshot's `duesStudentsCount` when unfiltered. */
  dueStudents: z.number().int(),
  /** Students with an overdue balance — the snapshot's `defaultersCount` when unfiltered. */
  overdueStudents: z.number().int(),
});
export type FeeStudentsSummary = z.infer<typeof FeeStudentsSummarySchema>;

export const FeeStudentsResponseSchema = z.object({
  rows: z.array(FeeStudentRowSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
  totalPages: z.number().int(),
  summary: FeeStudentsSummarySchema,
  academic: z.string().nullable(),
});
export type FeeStudentsResponse = z.infer<typeof FeeStudentsResponseSchema>;

// ── GET /fees/dues/by-class ──────────────────────────────────────────────────

export const FeeDuesByClassQuerySchema = z.object({
  academic: z.preprocess(feeQueryBlankToUndefined, academicLabel.optional()),
  studentType: z.preprocess(
    (v) => (v === "all" ? undefined : feeQueryBlankToUndefined(v)),
    z.enum(FEE_STUDENT_TYPES).optional(),
  ),
  /** Only the overdue slice (the dashboard's FEE OVERDUE deep link). */
  overdueOnly: z.preprocess(feeQueryBlankToUndefined, queryBool.optional()),
});
export type FeeDuesByClassQuery = z.output<typeof FeeDuesByClassQuerySchema>;

export const FeeDuesByClassRowSchema = z.object({
  classId: z.string().nullable(),
  className: z.string().nullable(),
  section: z.string().nullable(),
  /** Students in this classroom who still owe. */
  students: z.number().int(),
  due: money,
  overdue: money,
});
export type FeeDuesByClassRow = z.infer<typeof FeeDuesByClassRowSchema>;

export const FeeDuesByClassResponseSchema = z.object({
  rows: z.array(FeeDuesByClassRowSchema),
  totals: z.object({ students: z.number().int(), due: money, overdue: money, classrooms: z.number().int() }),
  academic: z.string().nullable(),
});
export type FeeDuesByClassResponse = z.infer<typeof FeeDuesByClassResponseSchema>;

// ── GET /fees/collections (the transactions ledger) ──────────────────────────

export const FEE_COLLECTION_SORTS = ["at", "amount"] as const;

const collectionsBase = z.object({
  /** IST days, inclusive. Omit both for all time (the Transactions "All time" switch). */
  from: z.preprocess(feeQueryBlankToUndefined, feeDayKey.optional()),
  to: z.preprocess(feeQueryBlankToUndefined, feeDayKey.optional()),
  /** `YYYY-YY` — narrows to that year's rows with the snapshot's own scope. */
  academic: z.preprocess(feeQueryBlankToUndefined, academicLabel.optional()),
  method: optionalText(32),
  /** The collecting user's id (`collectedByUserId`). */
  collector: z.preprocess(feeQueryBlankToUndefined, idParam.optional()),
  /** The head the money was DIRECTED at, else the row's own single head. */
  head: z.preprocess(feeQueryBlankToUndefined, z.enum(FEE_HEADS).optional()),
  /** Receipt number, student name or admission no. */
  search: optionalText(120),
});

function refineWindow(v: { from?: string; to?: string }, ctx: z.RefinementCtx): void {
  if (v.from && v.to && v.from > v.to) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["from"], message: "from must not be after to" });
  }
  if ((v.from && !v.to) || (!v.from && v.to)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["to"], message: "send both from and to, or neither" });
  }
}

export const FeeCollectionsFilterSchema = collectionsBase.superRefine(refineWindow);
export type FeeCollectionsFilter = z.output<typeof FeeCollectionsFilterSchema>;

export const FEE_COLLECTIONS_MAX_PAGE_SIZE = 200;

export const FeeCollectionsQuerySchema = collectionsBase
  .extend({
    sort: z.preprocess(feeQueryBlankToUndefined, z.enum(FEE_COLLECTION_SORTS).default("at")),
    dir: z.preprocess(feeQueryBlankToUndefined, z.enum(["asc", "desc"]).default("desc")),
    page: z.preprocess(feeQueryBlankToUndefined, z.coerce.number().int().min(1).max(100_000).default(1)),
    pageSize: z.preprocess(
      feeQueryBlankToUndefined,
      z.coerce.number().int().min(1).max(FEE_COLLECTIONS_MAX_PAGE_SIZE).default(25),
    ),
  })
  .superRefine(refineWindow);
export type FeeCollectionsQuery = z.output<typeof FeeCollectionsQuerySchema>;
export type FeeCollectionsQueryInput = z.input<typeof FeeCollectionsQuerySchema>;

export const FeeCollectionRowSchema = z.object({
  /** The `FeePaymentCollection` id, or `opening:<feePaymentId>` for money a row carries that no collection event accounts for. */
  id: z.string(),
  /** Null for the synthesised opening deposit — it has no per-collection receipt, only the installment's. */
  collectionId: z.string().nullable(),
  feePaymentId: z.string(),
  /** COLLECTION = money in; CORRECTION = an approved reversal (negative); OPENING = a deposit that predates the event ledger. */
  kind: z.enum(["COLLECTION", "CORRECTION", "OPENING"]),
  /** ISO instant. */
  at: z.string(),
  /** IST day `YYYY-MM-DD`. */
  day: z.string(),
  /** Signed rupees: a correction is negative. */
  amount: money,
  method: z.string().nullable(),
  reference: z.string().nullable(),
  collectorId: z.string().nullable(),
  collectorName: z.string().nullable(),
  /** Directed head, else the row's own single head; null = a bundle installment nobody directed. */
  head: z.string().nullable(),
  /** The deposit's one receipt number when it has one, else the installment's. */
  receiptNo: z.string().nullable(),
  /** The payment this event belongs to: a collect that settled several installments is ONE deposit with one receipt. Null for an opening deposit and for events written before deposits were grouped. */
  depositGroupId: z.string().nullable(),
  depositReceiptNo: z.string().nullable(),
  /** `PENDING | CLEARED | BOUNCED` for a cheque, else null. */
  clearanceStatus: z.string().nullable(),
  studentId: z.string(),
  studentName: z.string(),
  admissionNo: z.string(),
  className: z.string().nullable(),
  section: z.string().nullable(),
  /** The fee item this money went against. */
  item: z.string(),
  /** False when the student has since left the roll — the receipt stays on the ledger. */
  onRoll: z.boolean(),
});
export type FeeCollectionRow = z.infer<typeof FeeCollectionRowSchema>;

export const FeeCollectionFacetSchema = z.object({
  key: z.string(),
  label: z.string().nullable(),
  amount: money,
  count: z.number().int(),
});

export const FeeCollectionsResponseSchema = z.object({
  rows: z.array(FeeCollectionRowSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
  totalPages: z.number().int(),
  /** Over EVERY matching event, not just this page. `net = collected + reversed`. */
  totals: z.object({ net: money, collected: money, reversed: money, count: z.number().int() }),
  /** Counts and sums per method / collector for the window, ignoring the method and collector filters themselves so the chips keep their numbers. */
  facets: z.object({
    methods: z.array(FeeCollectionFacetSchema),
    collectors: z.array(FeeCollectionFacetSchema),
  }),
  window: z.object({ from: z.string().nullable(), to: z.string().nullable(), academic: z.string().nullable() }),
});
export type FeeCollectionsResponse = z.infer<typeof FeeCollectionsResponseSchema>;

// ── GET /fees/reports/daily-register ─────────────────────────────────────────

export const FeeDailyRegisterQuerySchema = z.object({
  /** IST day. Defaults to today in the route. */
  day: z.preprocess(feeQueryBlankToUndefined, feeDayKey.optional()),
});
export type FeeDailyRegisterQuery = z.output<typeof FeeDailyRegisterQuerySchema>;

const RegisterLineSchema = z.object({
  key: z.string(),
  label: z.string().nullable(),
  amount: money,
  count: z.number().int(),
});

export const FeeDailyRegisterResponseSchema = z.object({
  day: z.string(),
  /** Net of corrections, the same fold the Finance "Today's Collections" tile prints. */
  totals: z.object({ net: money, collected: money, reversed: money, count: z.number().int(), receipts: z.number().int() }),
  byMethod: z.array(RegisterLineSchema),
  /** `key` is the collecting user's id, or `""` for money nobody at the counter took (gateway, manual queue, imports). */
  byCollector: z.array(
    RegisterLineSchema.extend({
      /** Net CASH this collector took — what they are expected to hand over. */
      cash: money,
    }),
  ),
  /** `key` is a fee head, or `""` for a bundle installment whose payment was not directed at one head. */
  byHead: z.array(RegisterLineSchema),
});
export type FeeDailyRegisterResponse = z.infer<typeof FeeDailyRegisterResponseSchema>;

// ── GET /fees/reports/head-wise ──────────────────────────────────────────────

export const FeeHeadWiseQuerySchema = z.object({
  academic: z.preprocess(feeQueryBlankToUndefined, academicLabel.optional()),
});
export type FeeHeadWiseQuery = z.output<typeof FeeHeadWiseQuerySchema>;

const HeadLineSchema = z.object({
  /** A fee head; `null` = a bundle installment with no resolvable composition. */
  head: z.string().nullable(),
  billed: money,
  concession: money,
  fine: money,
  net: money,
  paid: money,
  due: money,
});

export const FeeHeadWiseResponseSchema = z.object({
  academic: z.string().nullable(),
  heads: z.array(HeadLineSchema),
  /** Bundles that could not be split by head, so nobody reads a guess as a fact. */
  several: HeadLineSchema.omit({ head: true }).nullable(),
  /** Money collected beyond what rows were billed (advances parked on the last installment). */
  advance: money,
  /** The carried-forward `PreviousFeeDue` balance — outside every head and every year. */
  previousDue: money,
  /** `net` and `paid` are over EVERY student (cash is permanent); `due` is students on the roll only. */
  totals: z.object({ billed: money, concession: money, fine: money, net: money, paid: money, due: money }),
});
export type FeeHeadWiseResponse = z.infer<typeof FeeHeadWiseResponseSchema>;

// ── GET /fees/reports/ageing ─────────────────────────────────────────────────

export const FEE_AGE_BUCKET_KEYS = ["current", "d0_30", "d31_60", "d61_90", "d91_180", "d180_plus"] as const;
export type FeeAgeBucketKey = (typeof FEE_AGE_BUCKET_KEYS)[number];

export const FeeAgeingQuerySchema = z.object({
  academic: z.preprocess(feeQueryBlankToUndefined, academicLabel.optional()),
});
export type FeeAgeingQuery = z.output<typeof FeeAgeingQuerySchema>;

export const FeeAgeingResponseSchema = z.object({
  academic: z.string().nullable(),
  /** Row-exact with `buildFeeSnapshot().aging`: same rows, same IST-day cutoff. `count` is fee rows, `students` is distinct students. */
  buckets: z.array(
    z.object({
      key: z.enum(FEE_AGE_BUCKET_KEYS),
      amount: money,
      count: z.number().int(),
      students: z.number().int(),
    }),
  ),
  /** Carried-forward dues — past due by definition, and not in any bucket above. */
  previousDues: z.object({ amount: money, students: z.number().int() }),
  /** `Σ buckets + previousDues` — the snapshot's `outstandingTotal`. */
  total: money,
});
export type FeeAgeingResponse = z.infer<typeof FeeAgeingResponseSchema>;

// ── GET /fees/students.csv ───────────────────────────────────────────────────

/** Rows a single export will carry before it says so and stops. */
export const FEE_STUDENTS_CSV_ROW_CAP = 50_000;

// ── Day-close: a counter's day signed off (rule 16h) ─────────────────────────
//
//   GET    /fees/day-close?day=            every collector the register saw that day + their close
//   POST   /fees/day-close                 record the cash COUNTED for one collector's day
//   DELETE /fees/day-close/:id?reason=     reopen (CHAIRMAN / ADMIN); the row stays as history
//
// Expected cash is always computed on the server from the daily register
// (`byCollector[].cash`); a client never sends it. Money is rupee numbers on the
// wire (two decimals) — the table behind it is paise-only.

/** The most one counted-cash figure may carry (₹1,00,00,000): the same ceiling as `MAX_FEE_AMOUNT` in the API's fee math. */
export const FEE_DAY_CLOSE_MAX_CASH = 10_000_000;

const wholePaiseRupees = (n: number): boolean => {
  const paise = n * 100;
  return Number.isFinite(n) && Math.abs(paise - Math.round(paise)) < 1e-6;
};

/** Why a close / reopen / locked write is refused — the `error.code`s of the three routes and the lock. */
export const FEE_DAY_CLOSE_ERROR_CODES = [
  "FUTURE_DAY",
  "NOT_YOUR_COUNTER",
  "DAY_ALREADY_CLOSED",
  "DAY_ALREADY_OPEN",
  "DAY_CLOSED",
] as const;
export type FeeDayCloseErrorCode = (typeof FEE_DAY_CLOSE_ERROR_CODES)[number];

export const FeeDayCloseQuerySchema = z.object({
  /** IST day. Defaults to today in the route. */
  day: z.preprocess(feeQueryBlankToUndefined, feeDayKey.optional()),
});
export type FeeDayCloseQuery = z.output<typeof FeeDayCloseQuerySchema>;

/** One signed-off counter day. `variance = countedCash - expectedCashAtClose` (negative = short). */
export const FeeDayCloseSchema = z.object({
  id: z.string(),
  countedCash: money,
  variance: money,
  note: z.string().nullable(),
  closedByUserId: z.string(),
  closedByName: z.string(),
  closedAt: z.string(),
  /** The register's cash for the collector at the moment of closing; today's `expectedCash` may differ if the register moved after. */
  expectedCashAtClose: money,
});
export type FeeDayClose = z.infer<typeof FeeDayCloseSchema>;

export const FeeDayCloseCollectorSchema = z.object({
  /** `null` = money nobody at the counter took (gateway, manual queue, imports). */
  collectorId: z.string().nullable(),
  collectorName: z.string().nullable(),
  /** The register's net CASH for this collector today. */
  expectedCash: money,
  /** The register's event count for this collector. */
  receipts: z.number().int(),
  /** The open close, or `null` while the counter is still open. */
  close: FeeDayCloseSchema.nullable(),
});
export type FeeDayCloseCollector = z.infer<typeof FeeDayCloseCollectorSchema>;

export const FeeDayCloseResponseSchema = z.object({
  day: z.string(),
  collectors: z.array(FeeDayCloseCollectorSchema),
  closedCount: z.number().int(),
  openCount: z.number().int(),
});
export type FeeDayCloseResponse = z.infer<typeof FeeDayCloseResponseSchema>;

export const FeeDayCloseBodySchema = z.object({
  day: feeDayKey,
  /** The collecting user's id, or `null` for the unattributed counter. */
  collectorId: z.string().trim().min(1).max(64).nullable(),
  countedCash: z
    .number({ invalid_type_error: "Enter the cash counted." })
    .min(0, "Counted cash can't be negative.")
    .max(FEE_DAY_CLOSE_MAX_CASH, "That is more cash than a counter can hold.")
    .refine(wholePaiseRupees, "Amounts can't carry fractions of a paisa."),
  note: z.string().trim().max(500, "Keep the note under 500 characters.").optional(),
});
export type FeeDayCloseBody = z.output<typeof FeeDayCloseBodySchema>;

export const FeeDayReopenParamsSchema = z.object({ id: idParam });
export const FeeDayReopenQuerySchema = z.object({
  reason: z
    .string({ required_error: "Say why the day is being reopened." })
    .trim()
    .min(10, "Give a reason of at least 10 characters.")
    .max(500, "Keep the reason under 500 characters."),
});
export type FeeDayReopenQuery = z.output<typeof FeeDayReopenQuerySchema>;

/** `error.details` of a 409 `DAY_CLOSED`: which counter-day is locked and by whom. */
export interface FeeDayClosedDetails {
  day: string;
  collectorName: string | null;
  closedAt: string;
}
